import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import api from "lib/api/api";

const STATUS_LABELS = {
  COMPLETED: "Completed",
  IN_PROGRESS: "In progress",
  TIME_EXPIRED_RETAKE_REQUIRED: "Time expired / retake",
  ABANDONED: "Abandoned",
  READY_FOR_RE_EVALUATION: "Ready for re-evaluation",
  SUBMISSION_FAILED_RETAKE_REQUIRED: "Submission failed / retake",
  EMAIL_FAILED: "Email failed",
  EVALUATION_CONFLICT: "Evaluation conflict",
  EXISTING_RESULT_UNFINISHED_SESSION: "Unfinished duplicate",
  RETAKE_RESOLVED: "Retake released",
  SUPERSEDED: "Superseded",
};

const Metric = ({ label, value, note }) => (
  <Paper variant="outlined" sx={{ p: 2.25, minHeight: 120 }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h3" fontWeight={800} sx={{ my: 0.5 }}>{value ?? 0}</Typography>
    <Typography variant="caption" color="text.secondary">{note}</Typography>
  </Paper>
);

const BarChart = ({ title, subtitle, values = {}, labels = {} }) => {
  const entries = Object.entries(values);
  const maximum = Math.max(1, ...entries.map(([, value]) => Number(value) || 0));
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography variant="h6" fontWeight={750}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{subtitle}</Typography>
      <Stack spacing={1.5}>
        {entries.map(([key, value]) => (
          <Box key={key}>
            <Stack direction="row" justifyContent="space-between" gap={2}>
              <Typography variant="body2" fontWeight={600}>{labels[key] || key.replaceAll("_", " ")}</Typography>
              <Typography variant="body2" fontWeight={800}>{value}</Typography>
            </Stack>
            <Box sx={{ mt: 0.5, height: 12, bgcolor: "#edf0f2", borderRadius: 20, overflow: "hidden" }}>
              <Box sx={{ width: `${((Number(value) || 0) / maximum) * 100}%`, minWidth: value ? 8 : 0, height: "100%", bgcolor: "#ffc400", borderRadius: 20, transition: "width .25s ease" }} />
            </Box>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

const PlacementTestAnalytics = ({ endpoint = "/admin/settings/placement-test/analytics", embedded = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get(endpoint);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load placement-test analytics.");
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);
  const completionRate = useMemo(() => data?.totalSessions
    ? Math.round((Number(data.completedEvaluations || 0) / Number(data.totalSessions)) * 100)
    : 0, [data]);

  return (
    <Paper variant={embedded ? "outlined" : "elevation"} sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={800}>Placement Test Analytics</Typography>
          <Typography color="text.secondary">All production placement sessions, independent of recovery-table pagination and filters.</Typography>
        </Box>
        <Button variant="outlined" onClick={load} disabled={loading}>Refresh analytics</Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress /></Box> : (
        <Stack spacing={2.5}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 2 }}>
            <Metric label="Students who took the test" value={data?.uniqueStudents} note="Unique student accounts and verified guests" />
            <Metric label="Test attempts" value={data?.totalSessions} note="Every started placement session" />
            <Metric label="Submitted attempts" value={data?.submittedSessions} note="A persisted submitted answer exists" />
            <Metric label="Completed evaluations" value={data?.completedEvaluations} note={`${completionRate}% of all attempts`} />
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, 1fr)" }, gap: 2 }}>
            <BarChart title="Attempts by recovery status" subtitle="Operational state of every placement session" values={data?.sessionsByStatus} labels={STATUS_LABELS} />
            <Stack spacing={2}>
              <BarChart title="Attempts by target level" subtitle="Level selected when the test was started" values={data?.sessionsByTargetLevel} />
              <BarChart title="Evaluations by final level" subtitle="Final levels stored by the evaluation service" values={data?.evaluationsByFinalLevel} />
            </Stack>
          </Box>
        </Stack>
      )}
    </Paper>
  );
};

export default PlacementTestAnalytics;
